import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import DashboardTile from '../components/DashboardTile';
import api from '../utils/api';
import './UserDashboard.css';

const UserDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [featureToggles, setFeatureToggles] = useState(null);
  const [programCategories, setProgramCategories] = useState([]);

  // Load feature toggles based on user tier
  useEffect(() => {
    const fetchToggles = async () => {
      try {
        const response = await api.get('/api/feature-toggles');
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetched user tier feature toggles:', response.data);
          console.log('User tier:', response.data.userTier);
        }
        // Backend returns tier-specific toggles at root level based on authenticated user
        setFeatureToggles(response.data);
      } catch (error) {
        console.error('Error fetching feature toggles:', error);
        // If error, set all to false
        setFeatureToggles({ 
          education: false,
          health: false,
          harvesting: false, 
          communityResources: false,
          communityOutreach: false,
          council: false,
          hr: false,
          consultation: false
        });
      }
    };
    fetchToggles();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/program-categories?activeOnly=true');
        const categories = (response.data.categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        setProgramCategories(categories);
      } catch (err) {
        console.error('Error fetching program categories:', err);
        setProgramCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // No need to fetch applications anymore
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err.message);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        await api.get('/api/harvesting-posts/public');
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err.message);
        setError('Failed to load blog posts');
        setLoading(false);
      }
    };

    fetchData();
    fetchBlogPosts();
  }, []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-welcome">
        <h2>Welcome{currentUser ? `, ${currentUser.firstName || ''}` : ''}!</h2>
        <p>Your RSMIN portal dashboard</p>
      </div>
      
      {!featureToggles ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      ) : loading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-text">{error}</p>
        </div>
      ) : (
        <>
          {/* Programs & Services Section */}
          {(() => {
            const defaultDescriptions = {
              education: 'Scholarships, grants, and educational support',
              health: 'Medical services, wellness, and health support',
              culture: 'Cultural programs, language revitalization, and traditional practices',
              legal: 'Legal services, treaty rights, and advocacy support'
            };

            const visibleProgramCategories = programCategories.filter((category) => {
              if (category.key === 'education') return featureToggles.education;
              if (category.key === 'health') return featureToggles.health;
              if (category.key === 'culture') return featureToggles.culture;
              if (category.key === 'legal') return featureToggles.legal;
              return false; // Don't show unknown categories
            }).sort((a, b) => {
              const priority = ['education', 'health'];
              const aIdx = priority.indexOf(a.key);
              const bIdx = priority.indexOf(b.key);
              if (aIdx !== -1 || bIdx !== -1) {
                if (aIdx === -1) return 1;
                if (bIdx === -1) return -1;
                return aIdx - bIdx;
              }
              return (a.order || 0) - (b.order || 0);
            });

            if (visibleProgramCategories.length === 0) {
              return null;
            }

            return (
            <div className="dashboard-section">
              <h3 className="section-header">📋 Programs & Services</h3>
              <p className="section-description">Apply for grants, programs, and support services</p>
              <div className="dashboard-tiles-container">
                {visibleProgramCategories.map((category) => (
                  <DashboardTile
                    key={category.key}
                    title={category.label}
                    icon={category.icon || '📋'}
                    description={category.description || defaultDescriptions[category.key] || 'Browse available programs and services'}
                    path={`/health-programs?category=${category.key}`}
                    color={category.color || '#666666'}
                  />
                ))}
              </div>
            </div>
            );
          })()}
          
          {/* Community & Resources Section */}
          {(featureToggles.harvesting || featureToggles.communityResources || featureToggles.consultation) && (
            <div className="dashboard-section">
              <h3 className="section-header">🌍 Community & Resources</h3>
              <p className="section-description">Access community resources and information</p>
              <div className="dashboard-tiles-container">
                {/* Harvesting Tile */}
                {featureToggles.harvesting && (
                  <DashboardTile 
                    title="Harvesting" 
                    icon="🌾"
                    description="Harvesting information and blog updates" 
                    path="/harvesting"
                    color="#DB4437"
                  />
                )}
                
                {/* Community Resources Tile */}
                {featureToggles.communityResources && (
                  <DashboardTile 
                    title="Community Resources" 
                    icon="📚"
                    description="Access community resources and updates" 
                    path="/community-resources"
                    color="#AB47BC"
                  />
                )}
                
                {/* Consultation Tile */}
                {featureToggles.consultation && (
                  <DashboardTile 
                    title="Project Consultation" 
                    icon="🤝"
                    description="View community consultation projects" 
                    path="/consultation"
                    color="#F4B400"
                  />
                )}
              </div>
            </div>
          )}
          
          {/* Administration Section */}
          {(featureToggles.communityOutreach || featureToggles.council || featureToggles.hr || featureToggles.faq) && (
            <div className="dashboard-section">
              <h3 className="section-header">🏛️ Administration</h3>
              <p className="section-description">Council, staff, and organizational services</p>
              <div className="dashboard-tiles-container">
                {/* Community Outreach Tile */}
                {featureToggles.communityOutreach && (
                  <DashboardTile 
                    title="Community Outreach" 
                    icon="🤲"
                    description="Community outreach programs and initiatives" 
                    path="/staff/outreach"
                    color="#0F9D58"
                  />
                )}

                {/* FAQ Tile */}
                {featureToggles.faq && (
                  <DashboardTile 
                    title="FAQ" 
                    icon="❓"
                    description="Frequently asked questions" 
                    path="/faq"
                    color="#6B8E23"
                  />
                )}
                
                {/* Council Tile */}
                {featureToggles.council && (
                  <DashboardTile 
                    title="Council" 
                    icon="🏛️"
                    description="Council matters and governance" 
                    path="/staff/council"
                    color="#9C27B0"
                  />
                )}
                
                {/* HR Tile */}
                {featureToggles.hr && (
                  <DashboardTile 
                    title="Human Resources" 
                    icon="👥"
                    description="Human resources and staff management" 
                    path="/hr"
                    color="#FF6F00"
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserDashboard;